/**
 * Lib.Common: v-markdown directive
 *
 * @file
 * Sets an element's `innerHTML` with a Markdown formatted string, converted to HTML.
 *
 * @modifier _blank - Open links in a new tab
 * @modifier nolink - Do not autodetect `http://` or `https://` links
 *
 * @value {string} - The Markdown formatted string to render.
 *
 * @example
 * <template lang="pug">
 * .ib-row
 *     ib-field.ib-col.l6.fs7.monospace.outset-0(multiline, height="auto", v-model="value")
 *     article.ib-col.l6.fs7.inset-8(v-markdown="value")
 * </template>
 * <script>
 *     {
 *         data() {
 *             return {
 *                 value:
 *                     'Supported Markdown features:\n\n' +
 *                     '#### Headings\n\n' +
 *                     'Inline styles such as **bold**, *italic*, ***both***, ~~strikethrough~~, `monospace`, --subscript--, and ^^superscript^^.\n\n' +
 *                     '> Block quotes, including\n' +
 *                     '> > nested block quotes.\n\n' +
 *                     '```\n' +
 *                     'Fenced code blocks\n' +
 *                     '```\n\n' +
 *                     '    Indented code blocks\n\n' +
 *                     '1. Numbered lists\n' +
 *                     '  - Unordered lists\n' +
 *                     '  - Nested in other lists\n' +
 *                     '    a. Lettered lists are an extension to the spec.\n' +
 *                     '    b. They may be useful for legal documents.\n' +
 *                     '2. Another entry in my numbered list.\n' +
 *                     '  i. Roman numeral lists are another extension.\n' +
 *                     '  ii. Romans built great roads, but their numbering system was not very efficient.\n' +
 *                     '  iii. We use it anyway for tables of contents.\n\n' +
 *                     '| Tables | Tables | Tables |\n' +
 *                     '| ------ | ------ | ------ |\n' +
 *                     '| Cell 1 | Cell 2 | Cell 3 |\n' +
 *                     '| Cell 4 | Cell 5 | Cell 6 |\n' +
 *                     '| Cell 7 | Cell 8 | Cell 9 |\n\n' +
 *                     '[Links](https://www.interactivebrokers.com)\n\n' +
 *                     'Images:\n\n' +
 *                     '![Images](/images/web/acct-type-joint.svg)\n\n' +
 *                     'Class lists, {.fg-error}\n' +
 *                     'including within the\n' +
 *                     'same paragraph. {.fg-ok}\n\n' +
 *                     '### Summary...\n\n' +
 *                     'These are the supported features:\n\n' +
 *                     '- Block quotes\n' +
 *                     '- Code blocks\n' +
 *                     '- Links\n' +
 *                     '- Images\n' +
 *                     '- Headings\n' +
 *                     '- Lists (including lettered lists and Roman numeral lists)\n' +
 *                     '- Bold\n' +
 *                     '- Italic\n' +
 *                     '- Strikethrough\n' +
 *                     '- Monospace\n' +
 *                     '- Subscript\n' +
 *                     '- Horizontal rule\n' +
 *                     '- Tables\n' +
 *                     '- Class lists (a la Markdown Extra attribute lists)\n'
 *             };
 *         }
 *     }
 * </script>
 */


import $markdown from '../global/markdown';


function bind(el, binding) {
    el.innerHTML = $markdown.toHTML(binding.value, {
        target: binding.modifiers._blank ? '_blank' : void 0,
        autolink: !binding.modifiers.nolink
    });
}

function unbind(el) {
    el.replaceChildren();
}

export default {
    name: 'markdown',
    directive: {
        bind,
        unbind,
        update: bind
    }
};
